import React from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { ExamDataProvider } from './contexts/ExamDataContext';
import { QuotaProvider } from './state/quotaStore';
import Navigation from './components/Navigation';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Signup from './components/Signup';
import ConfirmRegistration from './components/ConfirmRegistration';
import VerifyEmail from './components/VerifyEmail';
import ResetPassword from './components/ResetPassword';
import NewPassword from './components/NewPassword';
import Unsubscribe from './components/Unsubscribe';
import PracticeTestContainer from './containers/PracticeTestContainer';
import PerformanceHistory from './components/PerformanceHistory';
import SimulateExam from './components/SimulateExam';
import SimulateExamV2 from './components/SimulateExamV2';
import Settings from './components/Settings';
import Billing from './components/Billing';
import Usage from './components/Usage';
import Pricing from './components/Pricing';
import ExamTypeTest from './components/ExamTypeTest';
import SessionValidationTest from './components/SessionValidationTest';
import AIChat from './components/AIChat';
import FlashcardsPage from './components/flashcards/FlashcardsPage';
import QuotaModal from './components/billing/QuotaModal';

// Protected Route component
const ProtectedRoute = ({ children, requiresSubscription = true }) => {
  // Use the correct loading flag name from AuthContext to avoid premature redirects on refresh
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { hasActiveSubscription, isLoading: subscriptionLoading } = useSubscription();
  const location = useLocation();
  // Detect hard reload to avoid route change on refresh
  const navEntries = (typeof performance !== 'undefined' && performance.getEntriesByType)
    ? performance.getEntriesByType('navigation')
    : [];
  const isHardReload = (navEntries && navEntries[0]?.type === 'reload')
    || (typeof performance !== 'undefined' && performance.navigation && performance.navigation.type === 1);
  
  if (authLoading) {
    return <div>Loading...</div>; // You can replace this with a proper loading component
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect users without subscription to billing (except if already on billing or route opts out)
  // Routes with requiresSubscription={false} bypass this check
  if (requiresSubscription && !hasActiveSubscription && !subscriptionLoading && location.pathname !== '/billing') {
    // On a hard page refresh, render the current page instead of redirecting
    if (isHardReload) {
      return children;
    }
    return <Navigate to="/billing" replace />;
  }
  
  // Render children but show loading overlay if subscription is still loading
  return (
    <>
      {children}
      {subscriptionLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div>Loading subscription...</div>
        </div>
      )}
    </>
  );
};

// Layout component that conditionally renders the Navigation
const AppLayout = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const authPaths = ['/', '/login', '/create-account', '/confirm-registration', '/reset-password', '/pricing', '/unsubscribe', '/verify-email'];
  const isAuthPage = authPaths.includes(location.pathname) || location.pathname.startsWith('/reset-password/');
  const showNavigation = isAuthenticated && !isAuthPage;
  
  // Check if this is a practice test page or simulate exam page
  const isPracticeTestPage = location.pathname.startsWith('/practice-test');
  const isSimulateExamPage = location.pathname.startsWith('/simulate-exam');
  const isSpecialLayoutPage = isPracticeTestPage || isSimulateExamPage;
  const layoutClass = isSpecialLayoutPage ? 'app-layout practice-test-page-layout' : 'app-layout';

  return (
    <div className={layoutClass}>
      {showNavigation && <Navigation />}
      <div className={showNavigation ? 'content-with-nav' : 'content-full'}>
        {children}
      </div>
    </div>
  );
};

// App content component that uses auth context
const AppContent = () => {
  return (
    <SubscriptionProvider>
      <QuotaProvider>
        <ExamDataProvider>
          <div className="App">
            <AppLayout>
              <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/login" element={<Login />} />
                <Route path="/create-account" element={<Signup />} />
                <Route path="/confirm-registration" element={<ConfirmRegistration />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/reset-password/:token" element={<NewPassword />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/dashboard" element={<ProtectedRoute requiresSubscription={false}><Dashboard /></ProtectedRoute>} />
                <Route path="/practice-test" element={<ProtectedRoute><PracticeTestContainer /></ProtectedRoute>} />
                <Route path="/practice-test/:id" element={<ProtectedRoute><PracticeTestContainer /></ProtectedRoute>} />
                <Route path="/performance-history" element={<ProtectedRoute><PerformanceHistory /></ProtectedRoute>} />
                <Route path="/flashcards" element={<ProtectedRoute requiresSubscription={false}><FlashcardsPage /></ProtectedRoute>} />
                <Route path="/simulate-exam" element={<ProtectedRoute requiresSubscription={false}><SimulateExamV2 /></ProtectedRoute>} />
                <Route path="/simulate-exam-v1" element={<ProtectedRoute><SimulateExam /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute requiresSubscription={false}><Settings /></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute requiresSubscription={false}><Billing /></ProtectedRoute>} />
                <Route path="/usage" element={<ProtectedRoute requiresSubscription={false}><Usage /></ProtectedRoute>} />
                <Route path="/ai-chat" element={<ProtectedRoute requiresSubscription={false}><AIChat /></ProtectedRoute>} />
                <Route path="/examtypetest" element={<ProtectedRoute><ExamTypeTest /></ProtectedRoute>} />
                <Route path="/session-test" element={<ProtectedRoute><SessionValidationTest /></ProtectedRoute>} />
              </Routes>
            </AppLayout>
            {/* Global quota modal */}
            <QuotaModal />
          </div>
        </ExamDataProvider>
      </QuotaProvider>
    </SubscriptionProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
